import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import {
  ADD_USER,
  GET_USERS,
  REMOVE_USERS,
  RESET_USERS
} from 'actions/users/constants';
import {
  getUsers,
  getUsersSuccess,
  getUsersError,
  addUserSuccess,
  addUserError,
  removeUsersSuccess,
  removeUsersError,
  resetUsersSuccess,
  resetUsersError
} from 'actions/users';
import { hideModal } from 'actions/modal';

import normaliseError from 'utils/normaliseError';

import { API_URL, HTTP_VERBS } from 'utils/constants';

/* Get project users */
export function* getUsersSaga({ projectId, callback = () => {} }) {
  const requestUrl = `${API_URL}/projects/${projectId}/users`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getUsersSuccess(projectId, response.data.data));
    yield call(callback);
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getUsersError(projectId, transformedError));
  }
}

export function* getUsersWatcher() {
  yield takeLatest(GET_USERS, getUsersSaga);
}

/* Add project user */
export function* addUserSaga({ projectId, user, actions }) {
  const { firstName, lastName, ...fields } = user;

  const amendedUser = {
    ...fields,
    first_name: firstName,
    last_name: lastName
  };

  const requestUrl = `${API_URL}/projects/${projectId}/users`;
  const opts = {
    method: HTTP_VERBS.POST,
    url: requestUrl,
    data: JSON.stringify(amendedUser)
  };

  try {
    yield call(axios, opts);
    yield put(addUserSuccess(projectId));
    yield put(getUsers(projectId));
    yield put(hideModal());
    yield call(toastr.success, 'User added'); // TODO: use user details when backend passes them
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(addUserError(projectId, transformedError));
    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* addUserWatcher() {
  yield takeLatest(ADD_USER, addUserSaga);
}

/* Remove project users */
export function* removeUsersSaga({ projectId, userIds }) {
  const payload = {
    users: userIds
  };

  const requestUrl = `${API_URL}/projects/${projectId}/users/delete`;
  const opts = {
    method: HTTP_VERBS.POST,
    url: requestUrl,
    data: JSON.stringify(payload)
  };

  try {
    yield call(axios, opts);
    yield put(removeUsersSuccess(projectId));
    yield put(getUsers(projectId));
    yield call(toastr.success, 'User removed');
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(removeUsersError(projectId, transformedError));
    yield call(toastr.error, 'There was a problem removing the user');
  }
}

export function* removeUsersWatcher() {
  yield takeLatest(REMOVE_USERS, removeUsersSaga);
}

/* Reset project users */
export function* resetUsersSaga({ projectId, userIds }) {
  const payload = {
    users: userIds
  };

  const requestUrl = `${API_URL}/projects/${projectId}/users/invalidate_password`;
  const opts = {
    method: HTTP_VERBS.PUT,
    url: requestUrl,
    data: JSON.stringify(payload)
  };

  try {
    yield call(axios, opts);
    yield put(resetUsersSuccess(projectId));
    yield put(getUsers(projectId));
    yield call(toastr.success, 'Password reset');
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(resetUsersError(projectId, transformedError));
    yield call(toastr.error, 'There was a problem resetting the password');
  }
}

export function* resetUsersWatcher() {
  yield takeLatest(RESET_USERS, resetUsersSaga);
}

export default function* rootSaga() {
  yield all([
    call(getUsersWatcher),
    call(addUserWatcher),
    call(removeUsersWatcher),
    call(resetUsersWatcher)
  ]);
}
